import React from "react";
import EnvironmentPill from "../../../../components/EnvironmentPill";
import EyeIcon from "../../../../components/EyeIcon";

const shellClass =
  "w-full max-w-[560px] sm:max-w-[520px] md:max-w-[600px] lg:max-w-[560px] rounded-[18px] border border-black/5 bg-white p-6 sm:p-8 md:p-12 lg:p-10 shadow-[0_18px_44px_rgba(0,0,0,0.14)]";

export default function SSOLoginForgotPanel(props) {
  const {
    forgotStep,
    error,
    isLoading,
    email,
    forgotCode,
    setForgotCode,
    forgotNewPassword,
    setForgotNewPassword,
    forgotConfirmPassword,
    setForgotConfirmPassword,
    showForgotNewPassword,
    setShowForgotNewPassword,
    handleForgotRequestSubmit,
    handleForgotConfirmSubmit,
    handleForgotBack,
  } = props;

  return (
    <div className={shellClass} role="main" aria-labelledby="forgot-title">
      <h1 id="forgot-title" className="text-xl sm:text-2xl md:text-3xl lg:text-[38px] font-extrabold text-[#3A4A51] leading-tight">
        Reset your password
      </h1>
      <div className="mt-4 flex justify-center">
        <EnvironmentPill />
      </div>
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md animate-notice-slide-in">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      {forgotStep === "request" && (
        <form onSubmit={handleForgotRequestSubmit} className="mt-8">
          <p className="text-sm text-slate-600 mb-4">
            Enter your email and we&apos;ll send you a code to reset your password.
          </p>
          <label htmlFor="forgot-email" className="text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D]">
            EMAIL ADDRESS
          </label>
          <input
            id="forgot-email"
            type="email"
            value={email}
            readOnly
            className="mt-2 h-12 sm:h-[54px] w-full rounded-[4px] border-2 border-slate-200 bg-slate-50 px-3 sm:px-4 text-sm sm:text-base text-slate-600"
            aria-describedby="forgot-email-help"
          />
          <p id="forgot-email-help" className="mt-1 text-xs text-slate-500">Use the same email you use to sign in.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleForgotBack}
              className="order-2 sm:order-1 flex-1 h-12 rounded-[4px] border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2"
            >
              Back to sign in
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="order-1 sm:order-2 flex-1 h-12 rounded-[4px] bg-[#2B7F8D] text-white font-bold hover:bg-[#256f7a] focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading ? "Sending…" : "Send reset code"}
            </button>
          </div>
        </form>
      )}
      {forgotStep === "confirm" && (
        <form onSubmit={handleForgotConfirmSubmit} className="mt-8">
          <p className="text-sm text-slate-600 mb-4">
            Check your email for a verification code, then enter it below with your new password.
          </p>
          <label htmlFor="forgot-code" className="text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D]">
            VERIFICATION CODE
          </label>
          <input
            id="forgot-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={forgotCode}
            onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="mt-2 h-12 w-full rounded-[4px] border-2 border-slate-300 px-3 sm:px-4 text-base tracking-widest"
            placeholder="000000"
            maxLength={6}
            aria-label="Verification code"
          />
          <label htmlFor="forgot-new-password" className="mt-4 block text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D]">
            NEW PASSWORD
          </label>
          <div className="relative mt-2">
            <input
              id="forgot-new-password"
              type={showForgotNewPassword ? "text" : "password"}
              value={forgotNewPassword}
              onChange={(e) => setForgotNewPassword(e.target.value)}
              className="h-12 sm:h-[54px] w-full rounded-[4px] border-2 border-slate-300 px-3 sm:px-4 pr-12 text-sm sm:text-base outline-none focus:border-[#2B7F8D]"
              placeholder="Enter new password"
              autoComplete="new-password"
              aria-label="New password"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#2B7F8D]"
              onClick={() => setShowForgotNewPassword((v) => !v)}
              aria-label={showForgotNewPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon open={showForgotNewPassword} />
            </button>
          </div>
          <label htmlFor="forgot-confirm-password" className="mt-4 block text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D]">
            CONFIRM PASSWORD
          </label>
          <input
            id="forgot-confirm-password"
            type="password"
            value={forgotConfirmPassword}
            onChange={(e) => setForgotConfirmPassword(e.target.value)}
            className="mt-2 h-12 sm:h-[54px] w-full rounded-[4px] border-2 border-slate-300 px-3 sm:px-4 text-sm sm:text-base outline-none focus:border-[#2B7F8D]"
            placeholder="Confirm new password"
            autoComplete="new-password"
            aria-label="Confirm new password"
          />
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleForgotBack}
              className="order-2 sm:order-1 flex-1 h-12 rounded-[4px] border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2"
            >
              Back to sign in
            </button>
            <button
              type="submit"
              disabled={isLoading || !forgotCode.trim() || !forgotNewPassword || forgotNewPassword !== forgotConfirmPassword}
              className="order-1 sm:order-2 flex-1 h-12 rounded-[4px] bg-[#2B7F8D] text-white font-bold hover:bg-[#256f7a] focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading ? "Resetting…" : "Reset password"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
